import React, { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { ChevronLeft, ChevronRight, CheckCircle2, Eye, AlertCircle, X, Plus, Calendar} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday} from 'date-fns';
import { he, tr } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import AddTask from '@/components/AddTask';

const eventTypeIcons = {
  visit: Eye,
  progress: CheckCircle2,
  fault: AlertCircle,
};

const eventTypeLabels = {
  visit: 'ביקור',
  progress: 'שאלה',
  fault: 'תקלה',
};

export default function CalendarPage() {
  const { projects, events, tasks, toggleTask } = useApp();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showAddTask, setShowAddTask] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days: Date[] = [];
  let day = calendarStart;
  while (day <= calendarEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  // Memoized task map for performance
  const tasksByDate = useMemo(() => {
    const map = new Map<string, typeof tasks>();
    tasks.forEach(task => {
      const existing = map.get(task.scheduledDate) || [];
      map.set(task.scheduledDate, [...existing, task]);
    });
    return map;
  }, [tasks]);

  // Memoized events map for performance
  const eventsByDate = useMemo(() => {
    const map = new Map<string, typeof events>();
    events.forEach(event => {
      const existing = map.get(event.date) || [];
      map.set(event.date, [...existing, event]);
    });
    return map;
  }, [events]);

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return eventsByDate.get(dateStr) || [];
  };

  const getTasksForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return tasksByDate.get(dateStr) || [];
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];
  const selectedTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">לוח שנה</h1>
      </header>

      {/* Calendar */}
      <div className="bg-card rounded-2xl border border-border shadow-soft overflow-hidden mb-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <h2 className="font-bold text-lg">
            {format(currentMonth, 'MMMM yyyy', { locale: he })}
          </h2>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'].map((d) => (
            <div key={d} className="py-3 text-center text-sm font-medium text-muted-foreground">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const dayEvents = getEventsForDate(d);
            const dayTasks = getTasksForDate(d);
            const hasItems = dayEvents.length > 0 || dayTasks.length > 0;
            const isSelected = selectedDate && isSameDay(d, selectedDate);
            const incompleteTasks = dayTasks.filter(t => !t.isCompleted).length;

            return (
              <button
                key={i}
                onClick={() => setSelectedDate(d)}
                className={cn(
                  "aspect-square p-1 md:p-2 border-b border-l border-border flex flex-col items-center justify-start transition-colors relative",
                  !isSameMonth(d, currentMonth) && "opacity-40",
                  isToday(d) && "bg-primary/5",
                  isSelected && "bg-primary/10"
                )}
              >
                <span className={cn(
                  "w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full text-sm font-medium",
                  isToday(d) && "bg-primary text-primary-foreground"
                )}>
                  {format(d, 'd')}
                </span>
                {hasItems && (
                  <div className="flex gap-0.5 mt-1">
                    {incompleteTasks > 0 && (
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" title={`${incompleteTasks} משימות`} />
                    )}
                    {dayTasks.some(t => t.isCompleted) && (
                      <div className="w-1.5 h-1.5 rounded-full bg-success" />
                    )}
                    {dayEvents.length > 0 && (
                      <div className="w-1.5 h-1.5 rounded-full bg-accent-foreground/50" />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day Drawer */}
      {selectedDate && (
        <div className="fixed inset-x-0 bottom-0 md:relative md:inset-auto bg-card rounded-t-2xl md:rounded-2xl border border-border shadow-soft max-h-[60vh] md:max-h-none overflow-hidden animate-slide-up z-40">
          <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
            <h3 className="font-bold">
              {format(selectedDate, 'EEEE, d בMMMM', { locale: he })}
            </h3>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAddTask(true)}
                className="gap-1"
              >
                <Plus className="w-4 h-4" />
                משימה חדשה
              </Button>
              <button
                onClick={() => setSelectedDate(null)}
                className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 overflow-y-auto max-h-[50vh] md:max-h-80">
            {/* Tasks */}
            {selectedTasks.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">מתוכנן</h4>
                <div className="space-y-2">
                  {selectedTasks.map((task) => {
                    const project = projects.find(p => p.id === task.projectId);
                    return (
                      <div
                        key={task.id}
                        className="flex items-center gap-3 p-3 bg-secondary/50 rounded-xl"
                      >
                        <button
                          onClick={() => toggleTask(task.id)}
                          className={cn(
                            "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0",
                            task.isCompleted ? "bg-success border-success" : "border-primary hover:bg-primary/10"
                          )}
                        >
                          {task.isCompleted && <CheckCircle2 className="w-4 h-4 text-success-foreground" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "font-medium text-sm truncate",
                            task.isCompleted && "line-through text-muted-foreground"
                          )}>
                            {project?.name} - {task.title}
                          </p>
                          {task.notes && (
                            <p className="text-xs text-muted-foreground truncate">{task.notes}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleTask(task.id)}
                          className={cn(
                            "text-xs flex-shrink-0",
                            task.isCompleted ? "text-muted-foreground" : "text-primary"
                          )}
                        >
                          {task.isCompleted ? 'בטל' : 'בוצע'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Events */}
            {selectedEvents.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">תיעודים</h4>
                <div className="space-y-2">
                  {selectedEvents.map((event) => {
                    const Icon = eventTypeIcons[event.type];
                    const project = projects.find(p => p.id === event.projectId);
                    return (
                      <div
                        key={event.id}
                        className="flex items-start gap-3 p-3 bg-secondary/50 rounded-xl"
                      >
                        <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-success" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium">{eventTypeLabels[event.type]}</span>
                            <span className="text-xs text-muted-foreground">• {project?.name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{event.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedTasks.length === 0 && selectedEvents.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">אין משימות לתאריך זה</p>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => setShowAddTask(true)}
                  className="mt-2"
                >
                  הוסף משימה חדשה
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {showAddTask && (
        <AddTask open={showAddTask} onClose={() => setShowAddTask(false)} preselectedDate={selectedDate} />
      )}
    </div>
  );
}
